import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Messaging } from '@angular/fire/messaging';
import { deleteToken, getToken, onMessage } from 'firebase/messaging';
import { ToastController } from '@ionic/angular/standalone';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';

@Injectable({ providedIn: 'root' })
export class NotificationService {

  readonly notificationsDenied = signal(false);
  readonly fcmToken = signal<string | null>(null);
  readonly fcmPlatform = signal<'web' | 'android' | null>(null);

  private messaging = inject(Messaging);
  private router = inject(Router);
  private toastCtrl = inject(ToastController);
  private api = inject(ApiService);

  async init(): Promise<void> {
    // console.log('init of NotificationService');
    if (Capacitor.isNativePlatform()) {
      await this.initAndroid();
    } else {
      await this.initWeb();
    }
  }

  private async initWeb(): Promise<void> {
    try {
      // Check current state first — calling requestPermission() on a blocked origin throws in some browsers
      if (Notification.permission === 'denied') {
        this.notificationsDenied.set(true);
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        this.notificationsDenied.set(true);
        console.warn('FCM: notification permission denied');
        return;
      }
      // vapidKey must be added to environment.firebase in environment.dev.ts / environment.prod.ts
      // Find it in Firebase Console → Project Settings → Cloud Messaging → Web Push certificates
      const vapidKey = (environment.firebase as any)['vapidKey'];
      const token = await getToken(this.messaging, { vapidKey });
      console.log('FCM web token:', token);
      this.fcmToken.set(token);
      this.fcmPlatform.set('web');
      // callBackEndToStoreFCM — send token to PATCH /api/users/me/fcm-token when user is logged in

      // Fires: web browser only, app tab is open and active (foreground).
      // Never fires on Android; never fires when the tab is in background.
      onMessage(this.messaging, payload => {
        // Message is data-only (no notification block) — read from payload.data.
        const title = payload.data?.['title']       ?? 'New notification';
        const body  = payload.data?.['body']        ?? '';
        const url   = payload.data?.['urlToTakeTo'] ?? '/requests/inbox';
        const type  = payload.data?.['type']        ?? '';
        void this.showNotificationToast(title, body, url, type);
      });
    } catch (err) {
      console.error('FCM web token error:', err);
    }
  }

  private async initAndroid(): Promise<void> {
    // Requires: npm install @capacitor-firebase/messaging  +  npx cap sync android
    try {
      const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');

      await FirebaseMessaging.createChannel({
        id: 'connection-requests',
        name: 'Connection Requests',
        description: 'Notifications for new connection requests',
        importance: 4,
        visibility: 1,
        vibration: true,
        lights: true,
      });

      const { receive } = await FirebaseMessaging.requestPermissions();
      if (receive !== 'granted') {
        console.warn('FCM: push notification permission denied');
        return;
      }
      const { token } = await FirebaseMessaging.getToken();
      console.log('FCM android token:', token);
      this.fcmToken.set(token);
      this.fcmPlatform.set('android');
      // callBackEndToStoreFCM — send token to PATCH /api/users/me/fcm-token when user is logged in

      // Fires: Android only, app is in foreground.
      // MyFirebaseMessagingService.onMessageReceived() calls FirebaseMessagingPlugin.onMessageReceived()
      // only when isAppInForeground() is true. Never fires on web; never fires when app is backgrounded/killed.
      // Message is data-only — title and body are in data, not in event.notification.title/body.
      await FirebaseMessaging.addListener('notificationReceived', event => {
        const data  = event.notification.data as unknown as Record<string, string>;
        const title = data?.['title']        ?? 'New notification';
        const body  = data?.['body']         ?? '';
        const url   = data?.['urlToTakeTo']  ?? '/requests/inbox';
        const type  = data?.['type']         ?? '';
        void this.showNotificationToast(title, body, url, type);
      });

      // App was in background or terminated — user tapped the system notification
      await FirebaseMessaging.addListener('notificationActionPerformed', event => {
        const data = event.notification.data as unknown as Record<string, string>;
        const url  = data?.['urlToTakeTo'] ?? '/requests/inbox';
        this.markReadAndNavigate(url);
      });
    } catch (err) {
      console.error('FCM android token error:', err);
    }
  }

  private static readonly FCM_SENT_STATE_KEY = 'fcmSentState';

  private readSentState(): { uid: string; token: string } | null {
    try {
      const raw = localStorage.getItem(NotificationService.FCM_SENT_STATE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private writeSentState(uid: string, token: string): void {
    try {
      localStorage.setItem(NotificationService.FCM_SENT_STATE_KEY, JSON.stringify({ uid, token }));
    } catch {
      // non-fatal — next launch will retry
    }
  }

  private clearSentState(): void {
    try {
      localStorage.removeItem(NotificationService.FCM_SENT_STATE_KEY);
    } catch {
      // non-fatal
    }
  }

  async syncTokenToBackend(apiService: ApiService, uid: string): Promise<void> {
    const token = this.fcmToken();
    const platform = this.fcmPlatform();
    if (!token || !platform) {
      return;
    }
    const sent = this.readSentState();
    if (sent?.uid === uid && sent?.token === token) {
      return;
    }
    try {
      await firstValueFrom(apiService.patchFcmToken(token, platform));
      this.writeSentState(uid, token);
    } catch (err) {
      console.error('FCM sync to backend failed:', err);
    }
  }

  async clearTokenFromBackend(apiService: ApiService): Promise<void> {
    const platform = this.fcmPlatform();
    if (!platform) {
      return;
    }
    try {
      await firstValueFrom(apiService.deleteFcmToken(platform));
    } catch (err) {
      console.error('FCM clear from backend failed:', err);
    }
    try {
      if (platform === 'web') {
        await deleteToken(this.messaging);
      } else if (platform === 'android') {
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
        await FirebaseMessaging.deleteToken();
      }
    } catch (err) {
      console.error('FCM deleteToken failed:', err);
    }
    this.clearSentState();
    this.fcmToken.set(null);
    this.fcmPlatform.set(null);
  }

  private markReadAndNavigate(url: string): void {
    this.api.markNotificationReadByUrl(url).subscribe({ error: () => {} });
    // navigateByUrl parses the full URL including query params (e.g. ?action=accept).
    // router.navigate([url]) treats the whole string as a path segment and loses query params.
    void this.router.navigateByUrl(url);
  }

  private async showNotificationToast(title: string, body: string, url: string, type = ''): Promise<void> {
    const buttons = type === 'connection_request'
      ? [
          { text: 'Accept', handler: () => { this.markReadAndNavigate(url + '?action=accept'); } },
          { text: 'Ignore', role: 'cancel' },
          { text: 'Open',   handler: () => { this.markReadAndNavigate(url); } },
        ]
      : [
          { text: 'View',  handler: () => { this.markReadAndNavigate(url); } },
          { icon: 'close', role: 'cancel' },
        ];
    const toast = await this.toastCtrl.create({
      header: title,
      message: body,
      duration: 10000,
      position: 'top',
      layout: 'stacked',
      cssClass: 'notification-toast',
      buttons,
    });
    // present() adds the element to the DOM, so it must be called first.
    // requestAnimationFrame fires before the first visible paint, patching the shadow DOM
    // before the user sees the right-aligned default — minimises the flash.
    void toast.present();
    requestAnimationFrame(() => {
      const shadow = toast.shadowRoot;
      if (shadow) {
        const btnGroup = shadow.querySelector<HTMLElement>('.toast-button-group-end');
        if (btnGroup) {
          btnGroup.style.justifyContent = 'center';
        }
        const content = shadow.querySelector<HTMLElement>('.toast-content');
        if (content) {
          content.style.paddingBottom = '2px';
        }
      }
    });
  }
}
